import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/enhanced-button";
import { useToast } from "@/hooks/use-toast";
import { validateSession } from "@/services/auth.service";
import { initiateQuickBooksConnect } from "@/services/integration.service";
import qbLogo from "@/assets/images/integrations/qb-logo.svg";
import posthog, { captureError } from "@/lib/posthog";

const ConnectDataSource = () => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();

  useEffect(() => {
    const checkSession = async () => {
      const sessionData = await validateSession();
      if (!sessionData) {
        navigate("/", { replace: true });
        return;
      }
      if (!sessionData.organization) {
        navigate("/onboarding/organization", { replace: true });
        return;
      }
      if (sessionData.hasIntegration) {
        navigate("/dashboard", { replace: true });
        return;
      }
      setCheckingSession(false);
    };

    checkSession();
  }, [navigate]);

  // Handle error query param from failed QBO OAuth
  useEffect(() => {
    if (checkingSession) return;
    const integration = searchParams.get("integration");
    if (integration === "error") {
      const errorMessage =
        searchParams.get("message") || "Failed to connect QuickBooks. Please try again.";
      toast({
        title: "Integration Failed",
        description: errorMessage,
        variant: "destructive",
      });
    }
  }, [checkingSession, searchParams, toast]);

  const handleConnect = async () => {
    setIsConnecting(true);
    posthog.capture("integration_started", { provider: "quickbooks" });
    try {
      const authUrl = await initiateQuickBooksConnect();
      window.location.href = authUrl;
    } catch (error) {
      console.error("Failed to initiate QuickBooks connection:", error);
      posthog.capture("integration_connect_failed", { provider: "quickbooks" });
      captureError(error, { context: 'quickbooks_connect', provider: 'quickbooks' });
      toast({
        title: "Connection Error",
        description: "Failed to start QuickBooks connection. Please try again.",
        variant: "destructive",
      });
      setIsConnecting(false);
    }
  };

  if (checkingSession) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary flex items-center justify-center p-4">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <Card className="border-0 shadow-xl bg-card/95 backdrop-blur-sm">
          <CardContent className="p-8">
            <div className="mb-8 text-center">
              <div className="w-20 h-20 mx-auto mb-4">
                <img
                  src="/content/logo.png"
                  alt="BlinkBooks Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <h1 className="text-2xl font-bold text-foreground mb-2">
                Welcome to Blinkbooks
              </h1>
              <p className="text-muted-foreground mb-1">
                Let's connect your financial systems
              </p>
              <p className="text-sm text-muted-foreground">
                Blinkbooks unifies your accounting, payments, and banking data
                into one real-time financial view.
              </p>
            </div>

            <Card className="border shadow-sm">
              <CardContent className="p-6 flex flex-col gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex-shrink-0">
                    <img src={qbLogo} alt="QuickBooks" className="w-full h-full object-contain" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">QuickBooks Online</p>
                    <p className="text-sm text-muted-foreground">
                      Accounting & bookkeeping
                    </p>
                  </div>
                </div>

                <Button
                  onClick={handleConnect}
                  disabled={isConnecting}
                  className="w-full"
                  size="lg"
                >
                  {isConnecting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    "Connect QuickBooks"
                  )}
                </Button>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ConnectDataSource;
